/**
 * The bar along the hall's south wall: the counter, the kegs on the floor, the
 * lines up to the taps, the spills, and the table of clean glasses one of
 * which the sima goes into.
 *
 * The sima's own station is hashed; the beer is scenery.
 */

import {
  cylinder,
  decor,
  hose,
  type CylinderSpec,
  type DecorSpec,
  type HoseSpec,
} from '../world/Level';
import { tuoppi } from './levelJuhlasali';

/* ------------------------------------------------------ juhlasalin baari -- */

/**
 * The bar, in the south-west corner - the first thing on your right as you
 * come through the hall's doorway, and the only thing in the juhlasali that is
 * not a computer.
 *
 * It is against the south wall rather than out in the room, and that is the
 * whole of the constraint it was placed under. The wall-side lane of the
 * runway is `z = -31.975 .. -30.0` and the counter is 0.75 m deep against a
 * wall at z = -30.0, so what it leaves to run and to stand in is 1.23 m - the
 * narrowest the hall gets, and the reason it is that side of the room that
 * costs something. Everything here is dressing - decor, cylinders and hoses,
 * no solids - so none of it is in the fingerprint and, strictly, none of it is
 * in anybody's way either. See `src/config/CLAUDE.md`.
 *
 * The kegs stand on the floor beside it rather than under it because the
 * beer lines are the point: two hoses climbing out of two kegs, over the end
 * of the bar and along the top to the taps is what tells you the taps are
 * plumbed to something, and a keg tucked into a cupboard tells you nothing.
 *
 * The two badges on the wall finish that chain of custody - each tap is named
 * over the counter, so the line you can follow from a keg to a handle ends in
 * a word - and the beer on the floor and on the counter says that the whole
 * thing has been in use since last night, which is the only reason a bar in
 * this room is worth drawing at all.
 */
export const BAARI = {
  /** The counter. `wallZ` is the inside face of `navetta-etela-b`. */
  minX: 132.9,
  maxX: 135.9,
  frontZ: -30.75,
  wallZ: -30.0,
  /** Bar height, not table height: this is one you stand at. */
  top: 1.05,
  slab: 0.1,
  /** The two tap towers, and the two kegs each of them is fed from. */
  tapX: [133.45, 134.0],
  tapZ: -30.46,
  towerHeight: 0.3,
  kegX: [131.98, 132.52],
  kegZ: -30.35,
  kegRadius: 0.19,
  kegHeight: 0.58,
} as const;

/** Tuopit somebody poured at some point in the night and never came back for. */
const TUOPIT: readonly (readonly [number, number])[] = [
  [134.3, -30.62],
  [134.55, -30.28],
  [134.85, -30.55],
  [135.2, -30.3],
  [135.5, -30.58],
  [135.72, -30.22],
];

/**
 * What has run out of the kegs, and what has been slopped on the counter.
 *
 * `[x, z, radius]`, drawn as flat discs rather than as boxes: a spill has no
 * corners, and a cylinder six millimetres tall is the cheapest round thing in
 * the blockout. Both lists are authored as overlapping lobes for the same
 * reason - one circle reads as a beer mat, three of them read as something
 * nobody has come back with a mop for.
 *
 * The floor ones are the only dressing on this level that lies flat in the
 * middle of a floor, so they are kept east of x = 131.7 and inside z = -31.05:
 * the hall's west wall is at 131.55, and the wall-side lane starts at -31.975.
 * That does put them under the line that goes to the hanat, and it is meant
 * to - a runner who takes the side of the room the beer is on runs over what
 * has come out of the kegs, and it costs him nothing. What is avoided is the
 * other thing: a puddle out in the open floor, where a player who has never
 * seen this room before would read it as a hazard rather than as a mess.
 */
const KALJAA_LATTIALLA: readonly (readonly [number, number, number])[] = [
  // The pool the couplers have been dripping into, under and between the kegs.
  [132.25, -30.42, 0.34],
  [131.93, -30.56, 0.18],
  [132.55, -30.29, 0.2],
  // The tongue it has run out in, out past the end of the counter. Each lobe
  // overlaps the last: a chain of separate circles is a row of beer mats.
  [132.6, -30.62, 0.26],
  [132.9, -30.79, 0.19],
  [133.13, -30.9, 0.12],
  // And where somebody walked it out of the pool on the sole of a shoe.
  [133.42, -30.96, 0.1],
  [133.58, -30.9, 0.06],
];

/** The same, on the counter: `[x, z, radius]` at `BAARI.top`. */
const KALJAA_TISKILLA: readonly (readonly [number, number, number])[] = [
  // Under each nozzle, which is where a pour that misses the glass lands.
  [133.42, -30.63, 0.1],
  [133.55, -30.54, 0.05],
  [134.03, -30.66, 0.07],
  // And in among the tuopit, where they have been stood down and moved again.
  [134.42, -30.5, 0.07],
  [134.74, -30.4, 0.11],
  [134.87, -30.5, 0.06],
  [135.3, -30.45, 0.08],
  [135.63, -30.6, 0.05],
];

export function buildBaari(): {
  decor: DecorSpec[];
  cylinders: CylinderSpec[];
  hoses: HoseSpec[];
} {
  const items: DecorSpec[] = [];
  const drums: CylinderSpec[] = [];
  const lines: HoseSpec[] = [];
  const legTop = BAARI.top - BAARI.slab;

  // The counter: a top, a leg at each end, a back against the wall and a shelf
  // between the legs. Open at the front, so what is under it can be seen.
  items.push(
    decor('baaritiski-taso', 'wood', BAARI.minX, BAARI.frontZ, BAARI.maxX, BAARI.wallZ, BAARI.slab, legTop),
    decor('baaritiski-jalka-l', 'furniture', BAARI.minX, BAARI.frontZ + 0.05, BAARI.minX + 0.25, BAARI.wallZ - 0.05, legTop),
    decor('baaritiski-jalka-i', 'furniture', BAARI.maxX - 0.25, BAARI.frontZ + 0.05, BAARI.maxX, BAARI.wallZ - 0.05, legTop),
    decor('baaritiski-takalevy', 'furniture', BAARI.minX, BAARI.wallZ - 0.15, BAARI.maxX, BAARI.wallZ, legTop),
    decor('baaritiski-hylly', 'furniture', BAARI.minX + 0.25, BAARI.frontZ + 0.12, BAARI.maxX - 0.25, BAARI.wallZ - 0.15, 0.05, 0.36),
  );

  BAARI.tapX.forEach((x, i) => {
    const tower = BAARI.top + BAARI.towerHeight;
    drums.push(
      cylinder(`baarin-hana-${i + 1}-torni`, 'metal', x, BAARI.tapZ, 0.045, BAARI.towerHeight, BAARI.top, {
        radialSegments: 12,
      }),
      // A wooden handle, which is the one part of a tap you see from the door.
      cylinder(`baarin-hana-${i + 1}-kahva`, 'wood', x, BAARI.tapZ + 0.07, 0.018, 0.11, tower, {
        radialSegments: 8,
      }),
    );
    items.push(
      // The arm out over the counter, and the nozzle hanging off the end of it.
      decor(`baarin-hana-${i + 1}-varsi`, 'metal', x - 0.03, BAARI.tapZ - 0.22, x + 0.03, BAARI.tapZ, 0.06, tower - 0.09),
      decor(`baarin-hana-${i + 1}-nokka`, 'metal', x - 0.025, BAARI.tapZ - 0.25, x + 0.025, BAARI.tapZ - 0.19, 0.1, tower - 0.18),
    );
  });

  BAARI.kegX.forEach((x, i) => {
    drums.push(
      cylinder(`kaljakegi-${i + 1}`, 'metal', x, BAARI.kegZ, BAARI.kegRadius, BAARI.kegHeight, 0, {
        radialSegments: 14,
      }),
      // The chime the keg stands on and the one it is carried by, both a
      // little proud of the drum - which is the whole silhouette of a keg.
      cylinder(`kaljakegi-${i + 1}-alarengas`, 'metal', x, BAARI.kegZ, BAARI.kegRadius + 0.02, 0.06, 0, {
        radialSegments: 14,
      }),
      cylinder(`kaljakegi-${i + 1}-ylarengas`, 'metal', x, BAARI.kegZ, BAARI.kegRadius + 0.02, 0.06, BAARI.kegHeight - 0.06, {
        radialSegments: 14,
      }),
      cylinder(`kaljakegi-${i + 1}-liitin`, 'metal', x, BAARI.kegZ, 0.055, 0.09, BAARI.kegHeight, {
        radialSegments: 10,
      }),
    );

    // Out of the coupler, up over the end of the bar and along the top to the
    // tap. The two lines take different lanes across the counter so that the
    // second one does not run through the first one's tower.
    const lane = i === 0 ? -0.06 : 0.1;
    lines.push(
      hose(`kaljaletku-${i + 1}`, 'cable', 0.018, [
        { x, y: BAARI.kegHeight + 0.08, z: BAARI.kegZ },
        { x: x + 0.3, y: 0.88, z: BAARI.kegZ + lane * 0.5 },
        { x: BAARI.minX + 0.02 + i * 0.08, y: BAARI.top + 0.09, z: BAARI.kegZ + lane },
        { x: BAARI.tapX[i] - 0.28, y: BAARI.top + 0.035, z: BAARI.kegZ + lane },
        { x: BAARI.tapX[i], y: BAARI.top + 0.02, z: BAARI.tapZ },
      ]),
    );
  });

  // Both spills stand a few millimetres proud of the surface they lie on rather
  // than flush with it: two coplanar faces fight for the same pixels, and a
  // flickering puddle is worse than no puddle at all.
  KALJAA_LATTIALLA.forEach(([x, z, radius], i) => {
    drums.push(
      cylinder(`kaljalammikko-${i + 1}`, 'spill', x, z, radius, 0.006, 0.025, {
        radialSegments: 18,
      }),
    );
  });

  KALJAA_TISKILLA.forEach(([x, z, radius], i) => {
    drums.push(
      cylinder(`kaljatahra-${i + 1}`, 'spill', x, z, radius, 0.004, BAARI.top + 0.002, {
        radialSegments: 12,
      }),
    );
  });

  TUOPIT.forEach(([x, z], i) => {
    drums.push(...tuoppi(`tuoppi-${i + 1}`, x, z, BAARI.top, 1));
  });

  return { decor: items, cylinders: drums, hoses: lines };
}

/** Built once, for the same reason `COMPUTERS` is: it feeds three arrays. */
export const BAARI_PARTS = buildBaari();

/* ------------------------------------------- puhtaiden lasien pöytä ------ */

/**
 * The side table at the end of the baari with the clean glasses stacked on it
 * - and the one glass in the juhlasali that can be picked up.
 *
 * Every bar has one of these and nobody has ever had to be told what it is,
 * which is the whole reason it is here. The glass the porukka sends Kimmo for
 * used to stand a metre up pöytä 2, four metres past the hanat in among the
 * monitors, and the honest report from playing it was that a player who had
 * just been told to go and get a drink walked to the taps, found that F did
 * nothing there, and concluded the errand was not implemented. Both halves of
 * it are two metres apart on the same wall now, and what the toll costs is the
 * two seconds the pour takes. See `src/config/sima.ts` for that trade in full.
 *
 * It stands *east* of the counter, and that is what prices the errand: the
 * line overshoots the hanat, turns at the table and comes back two metres west
 * to pour, with the whole hall still in front of it. West of the counter would
 * have been passed on the way in and the round would collapse into one stride.
 *
 * Flush to the wall and only 42 cm deep, which is the second half of the same
 * argument. The wall-side lane is `z = -31.975 .. -30.0` and the counter
 * already takes 0.75 m of it; a table that reached as far out into the room as
 * the bar does would leave the tightest metre of the hall tighter still, at
 * the one place the run has to stop. A shallow console against a wall is also
 * simply what a rack of clean glasses stands on.
 *
 * This is what `nukkuja-2` used to decide, when he was asleep along the south
 * wall and this was whatever fitted in the slot behind him. He lies down the
 * middle of the room now, so the floor in front of the glasses is Kimmo's, and
 * `SIMA.reach` reaches them from anywhere in the lane rather than over a body.
 *
 * Lower and shallower than the counter, because it is a different piece of
 * furniture. A bar-height slab butted onto the end of a bar is a longer bar,
 * and a longer bar is not somewhere anybody looks for glasses.
 */
export const TUOPPIPOYTA = {
  /** Off the counter's east end by a gap you can see the wall through. */
  fromX: BAARI.maxX + 0.1,
  width: 0.9,
  /** Flush against the navetta's south wall, the same way `BAARI` is. */
  wallZ: BAARI.wallZ,
  /** Shallow, so it takes as little of the wall-side lane as a table can. */
  depth: 0.42,
  top: 0.78,
  slab: 0.05,
  legWidth: 0.06,
  legInset: 0.04,
  /**
   * The stacks: `[dx, dz, glasses]`, both offsets measured from the table's
   * north-west corner so that moving the table moves what is on it.
   *
   * Three of them and no two the same height. One even stack is a texture;
   * three uneven ones are a bar that has been drunk at since yesterday and
   * tidied by nobody. The tallest is the westernmost, nearest the hanat and
   * nearest the end of the counter, because that is the one a hand coming off
   * the lane reaches first - and it is the one F takes from.
   */
  stacks: [
    [0.18, 0.2, 4],
    [0.45, 0.12, 3],
    [0.72, 0.26, 2],
  ],
  /**
   * How far each glass sinks into the one under it, in metres.
   *
   * A stacked tuoppi is nested rather than balanced, so this is a good deal
   * less than `TUOPPI.height` - which is what makes the pile read as glassware
   * instead of as a column of separate glasses. It also buries every ring of
   * dregs but the top one, and that is the honest way to draw last night's
   * glasses stacked by somebody who was never going to wash them.
   */
  nesting: 0.055,
  /**
   * How much wider each glass is than the one it is sitting in.
   *
   * Without it a stack is one pale cylinder with no seams in it - every glass
   * is the same radius and the same material, and the rings of dregs are all
   * buried inside the glass below - so what the eye gets is a single tall
   * glass rather than four stacked ones. Seven per cent a glass steps the
   * silhouette visibly at the two metres this is looked at from, and it is
   * also simply what stacked glassware does: tumblers are tapered, so a pile
   * of them widens on the way up.
   */
  flare: 0.07,
} as const;

/** Which stack F takes from. See `stacks` - it is the one nearest the hanat. */
const SIMA_STACK = 0;

/**
 * Where the top glass of one stack stands.
 *
 * Both the cylinders and `SIMA_SPEC.glass` come through here, so the glass you
 * can see and the point F reaches for cannot drift apart. Same rule as the
 * muurikka's `station`.
 */
function tuoppipinonPaa(index: number): { x: number; y: number; z: number } {
  const [dx, dz, count] = TUOPPIPOYTA.stacks[index];
  return {
    x: TUOPPIPOYTA.fromX + dx,
    y: TUOPPIPOYTA.top + (count - 1) * TUOPPIPOYTA.nesting,
    z: TUOPPIPOYTA.wallZ - TUOPPIPOYTA.depth + dz,
  };
}

function buildTuoppipoyta(): { decor: DecorSpec[]; cylinders: CylinderSpec[] } {
  const T = TUOPPIPOYTA;
  const toX = T.fromX + T.width;
  const frontZ = T.wallZ - T.depth;
  const legTop = T.top - T.slab;

  // Four legs rather than the counter's two end panels: this one is a table,
  // and you can see under a table - which is what tells you at a glance that it
  // is a different piece of furniture from the slab beside it.
  const legs: readonly (readonly [number, number])[] = [
    [T.fromX + T.legInset, frontZ + T.legInset],
    [toX - T.legInset - T.legWidth, frontZ + T.legInset],
    [T.fromX + T.legInset, T.wallZ - T.legInset - T.legWidth],
    [toX - T.legInset - T.legWidth, T.wallZ - T.legInset - T.legWidth],
  ];
  const items: DecorSpec[] = [
    decor('tuoppipoyta-kansi', 'wood', T.fromX, frontZ, toX, T.wallZ, T.slab, legTop),
    ...legs.map(([x, z], i) =>
      decor(`tuoppipoyta-jalka-${i + 1}`, 'furniture', x, z, x + T.legWidth, z + T.legWidth, legTop),
    ),
  ];

  const drums: CylinderSpec[] = [];
  T.stacks.forEach(([dx, dz, count], stack) => {
    for (let i = 0; i < count; i++) {
      // The top of `SIMA_STACK` is the one F takes, and it is named for it:
      // `World` hides every `simatuoppi-*` the moment it is picked up, so what
      // is left standing on the table is the rest of the pile with one glass
      // gone off the top of it.
      const taken = stack === SIMA_STACK && i === count - 1;
      const name = taken ? 'simatuoppi' : `tuoppipino-${stack + 1}-${i + 1}`;
      drums.push(
        ...tuoppi(name, T.fromX + dx, frontZ + dz, T.top + i * T.nesting, 0, 1 + i * T.flare),
      );
    }
  });

  return { decor: items, cylinders: drums };
}

export const TUOPPIPOYTA_PARTS = buildTuoppipoyta();

/** The empty the porukka wants, in the middle of the glass rather than at its foot. */
export const SIMATUOPPI = tuoppipinonPaa(SIMA_STACK);
